import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MeetingRoomRounded,
  RefreshRounded,
  Search as SearchIcon,
} from "@mui/icons-material";
import { getProperties, type PropertyRecord } from "../api/properties";
import {
  createRoom,
  deleteRoom,
  getRooms,
  updateRoom,
  type RoomRecord,
} from "../api/rooms";
import { useNotifications } from "../components/NotificationProvider";

type RoomFormState = {
  property_id: string;
  room_number: string;
  floor_number: string;
  room_status: string;
  notes: string;
};

type RoomModalMode = "create" | "edit";

const createInitialFormState = (): RoomFormState => ({
  property_id: "",
  room_number: "",
  floor_number: "",
  room_status: "vacant",
  notes: "",
});

export function RoomsPage() {
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [properties, setProperties] = useState<PropertyRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<RoomModalMode>("create");
  const [selectedRoom, setSelectedRoom] = useState<RoomRecord | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<RoomRecord | null>(null);
  const [formState, setFormState] = useState<RoomFormState>(
    createInitialFormState,
  );
  const { showAlert, showSnackbar } = useNotifications();

  const loadProperties = async () => {
    try {
      const data = await getProperties();
      setProperties(data);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load properties",
        message: "Please refresh and try again.",
        variant: "error",
      });
    }
  };

  const loadRooms = async () => {
    setIsLoading(true);
    try {
      const data = await getRooms();
      setRooms(data);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load rooms",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadProperties();
    void loadRooms();
  }, []);

  const filteredRooms = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();
    if (!term) return rooms;

    return rooms.filter((room) => {
      const propertyName =
        properties.find((item) => item.id === room.property_id)
          ?.property_name ?? "";
      const haystack = [
        room.room_number,
        room.room_status,
        propertyName,
        room.notes ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [deferredSearchTerm, properties, rooms]);

  const openCreateModal = () => {
    setModalMode("create");
    setSelectedRoom(null);
    setFormState({
      ...createInitialFormState(),
      property_id: properties[0]?.id?.toString() ?? "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (room: RoomRecord) => {
    setModalMode("edit");
    setSelectedRoom(room);
    setFormState({
      property_id: String(room.property_id),
      room_number: room.room_number,
      floor_number: room.floor_number?.toString() ?? "",
      room_status: room.room_status,
      notes: room.notes ?? "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRoom(null);
    setFormState(createInitialFormState());
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        property_id: Number(formState.property_id),
        room_number: formState.room_number.trim(),
        floor_number: formState.floor_number
          ? Number(formState.floor_number)
          : null,
        room_status: formState.room_status,
        notes: formState.notes.trim() || null,
      };

      if (modalMode === "edit" && selectedRoom) {
        await updateRoom(selectedRoom.id, payload);
        showSnackbar({
          title: "Room updated",
          message: "The room details were updated successfully.",
          variant: "success",
        });
      } else {
        await createRoom(payload);
        showSnackbar({
          title: "Room added",
          message: "The room has been recorded successfully.",
          variant: "success",
        });
      }

      closeModal();
      await loadRooms();
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error
          ? error.message
          : "Please check the details and try again.";
      showAlert({
        title:
          modalMode === "edit" ? "Unable to update room" : "Unable to add room",
        message,
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteConfirmation = (room: RoomRecord) => {
    setRoomToDelete(room);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!roomToDelete) return;

    setIsLoading(true);
    try {
      await deleteRoom(roomToDelete.id);
      setDeleteDialogOpen(false);
      setRoomToDelete(null);
      await loadRooms();
      showSnackbar({
        title: "Room deleted",
        message: "The room has been removed.",
        variant: "info",
      });
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error
          ? error.message
          : "This room could not be removed.";
      showAlert({
        title: "Unable to delete room",
        message,
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%", position: "relative" }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: -8,
            left: 0,
            right: 0,
            borderRadius: 6,
          }}
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 6,
          p: { xs: 2, md: 3 },
          bgcolor: "background.paper",
          boxShadow: "0 10px 35px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by room, status, or property..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              maxHeight: 40,
              "& .MuiOutlinedInput-root": { borderRadius: 6 },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh rooms">
              <IconButton
                onClick={() => void loadRooms()}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 6,
                }}
              >
                <RefreshRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              disableElevation
              disabled={properties.length === 0}
              sx={{ borderRadius: 6, px: 3, whiteSpace: "nowrap", height: 40 }}
            >
              Add room
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 6, p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Rooms ({filteredRooms.length})
        </Typography>

        <Stack spacing={2}>
          {filteredRooms.map((room) => {
            const propertyName =
              properties.find((item) => item.id === room.property_id)
                ?.property_name ?? "Unknown property";

            return (
              <Card
                key={room.id}
                variant="outlined"
                sx={{
                  borderRadius: 6,
                  transition: "box-shadow 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    boxShadow: 2,
                    borderColor: "primary.main",
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    flexWrap: "wrap",
                    "&:last-child": { pb: 2 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Box
                        sx={{
                          p: 1.1,
                          borderRadius: 6,
                          bgcolor: "rgba(33, 82, 184, 0.08)",
                          color: "primary.main",
                        }}
                      >
                        <MeetingRoomRounded fontSize="small" />
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Room {room.room_number}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.2 }}
                        >
                          {propertyName}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1.25, flexWrap: "wrap" }}
                    >
                      <Chip
                        label={`Floor ${room.floor_number ?? "N/A"}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 6, fontWeight: 600 }}
                      />
                      <Chip
                        label={room.room_status}
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{
                          borderRadius: 6,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </Stack>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignSelf={{ xs: "flex-end", sm: "center" }}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => openEditModal(room)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => openDeleteConfirmation(room)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 6,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  {room.notes ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxWidth: 320 }}
                    >
                      {room.notes}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        {filteredRooms.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              No rooms match your current search.
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby="add-room-dialog-title"
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 560 } }}
      >
        <DialogTitle id="add-room-dialog-title">
          {modalMode === "edit" ? "Edit room" : "Add room"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a room record for an existing property using the fields from
            the schema.
          </DialogContentText>
          <Box
            component="form"
            id="add-room-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="room-property">Property</InputLabel>
                <Select
                  labelId="room-property"
                  value={formState.property_id}
                  label="Property"
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      property_id: event.target.value,
                    }))
                  }
                  sx={{ borderRadius: 6 }}
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.property_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                value={formState.room_number}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    room_number: event.target.value,
                  }))
                }
                label="Room number"
                required
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />

              <TextField
                value={formState.floor_number}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    floor_number: event.target.value,
                  }))
                }
                label="Floor number"
                type="number"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />

              <FormControl fullWidth>
                <InputLabel id="room-status-dialog">Status</InputLabel>
                <Select
                  labelId="room-status-dialog"
                  value={formState.room_status}
                  label="Status"
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      room_status: event.target.value,
                    }))
                  }
                  sx={{ borderRadius: 6 }}
                >
                  <MenuItem value="vacant">Vacant</MenuItem>
                  <MenuItem value="occupied">Occupied</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="reserved">Reserved</MenuItem>
                </Select>
              </FormControl>

              <TextField
                value={formState.notes}
                onChange={(event) =>
                  setFormState((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                label="Notes"
                multiline
                minRows={3}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} color="inherit" sx={{ borderRadius: 6 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-room-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            {modalMode === "edit" ? "Update room" : "Save room"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-room-dialog-title"
        PaperProps={{ sx: { borderRadius: 6 } }}
      >
        <DialogTitle id="delete-room-dialog-title">Delete room</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete room{" "}
            <strong>{roomToDelete?.room_number}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
